// term-types.ts — the terminal-appearance shapes the pi chat pipeline is
// keyed on. Extracted from helm-bridge's client types.ts with the pi-stack
// package split (Phase 1 T1.1 / BR1) because pi-theme.ts maps these onto the
// `--pi-*` custom-property set and both consumers (helm-bridge, helm-console)
// must agree on the shape. helm-bridge's types.ts re-exports these names so
// its existing import sites are unchanged.

import { CLAUDE_CHAT_FONT } from './pi-theme.js';

/** A single terminal color palette — the subset of xterm's ITheme that an
 * app theme's `chat` palette (helm-bridge themes.ts) fills in.
 * `buildXtermTheme()` maps this straight onto ITheme. */
export interface TerminalColors {
  background: string;
  foreground: string;
  cursor: string;
  cursorAccent: string;
  selectionBackground: string;
  selectionForeground?: string;
  black: string;
  red: string;
  green: string;
  yellow: string;
  blue: string;
  magenta: string;
  cyan: string;
  white: string;
  brightBlack: string;
  brightRed: string;
  brightGreen: string;
  brightYellow: string;
  brightBlue: string;
  brightMagenta: string;
  brightCyan: string;
  brightWhite: string;
}

/** Terminal/chat appearance prefs. Colors now come from the active APP THEME
 * (helm-bridge themes.ts, chosen per mode via prefs.themeDark/themeLight) —
 * the three colorScheme* fields below are retired preset slots kept only as
 * persisted passthrough for the server-side theme migration. Font settings
 * remain live (pi chat: code font + chat prose font). */
export interface TerminalPrefs {
  /** @deprecated retired single terminal-preset id (e.g. 'solarized-dark');
   * migration source only, superseded by prefs.themeDark/themeLight. */
  colorScheme?: string;
  /** @deprecated retired per-app-mode terminal preset slot, superseded by
   * helm-bridge `prefs.themeDark` (full app themes); persisted passthrough,
   * read once by the server-side theme migration. */
  colorSchemeDark: string;
  /** @deprecated see colorSchemeDark; superseded by `prefs.themeLight`. */
  colorSchemeLight: string;
  fontFamily: string;
  /** Chat prose font for pi message views (assistant answers). Defaults to
   * the Claude-desktop serif stack; the 'terminal' sentinel folds it back
   * onto `fontFamily`. Terminals ignore it. */
  chatFontFamily?: string;
  fontSize: number;
  fontWeight: 400 | 500;
  /** Weight for bold text runs (xterm fontWeightBold). Default 700; a font
   * whose bold face misregisters (browser then synthesizes a smeared bold —
   * seen with Nerd Font builds) can be pinned to a weight that maps to a
   * real face, down to "same as regular". */
  fontWeightBold?: number;
  cursorStyle: 'block' | 'bar' | 'underline';
  cursorBlink: boolean;
}

/** Single source of truth for the default terminal appearance — reused by the
 * client store default and referenced when reasoning about server defaults. */
export function defaultTerminalPrefs(): TerminalPrefs {
  return {
    colorScheme: 'solarized-dark',
    colorSchemeDark: 'solarized',
    colorSchemeLight: 'solarized',
    // NB: Nerd Fonts v3 registers the patched family as "JetBrainsMono Nerd
    // Font Mono" (no space in JetBrainsMono) — both spellings are listed so
    // either install resolves before falling through to plain JetBrains Mono.
    fontFamily: "'JetBrainsMono Nerd Font Mono','JetBrains Mono Nerd Font Mono','JetBrains Mono',Menlo,monospace",
    // Chat prose defaults to the Claude-desktop serif look (pi views only).
    chatFontFamily: CLAUDE_CHAT_FONT,
    fontSize: 13,
    fontWeight: 500,
    fontWeightBold: 700,
    cursorStyle: 'bar',
    cursorBlink: true,
  };
}
